import { Path, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UseFormProps, FieldValues } from "react-hook-form";
import { z } from "zod";

/**
 * Custom hook to handle form logic with validation and API error handling.
 * @param schema Zod schema for validation.
 * @param options react-hook-form options (optional).
 * @param formErrorsHandler Custom API error handler (optional).
 */
export function useFormHandler<T extends FieldValues>(
	schema: z.ZodType<T>,
	options: UseFormProps<T> = {},
	formErrorsHandler?: (errors: unknown) => void,
) {
	const form = useForm<T>({
		resolver: zodResolver(schema),
		...options,
	});

	const { setError } = form;

	/**
	 * Default From error handler
	 */
	const defaultFormErrorsHandler = (error: unknown) => {
		const isValidFieldName = (field: string): field is Path<T> => {
			return field in form.getValues();
		};
		const apiError = error as unknown as ApiError;

		if (apiError?.validationErrors) {
			Object.entries(apiError?.validationErrors).forEach(([fieldName, errorMessages]) => {
				if (isValidFieldName(fieldName)) {
					setError(fieldName, { message: errorMessages?.join(" ") });
				}
			});
		}
	};

	return {
		form,
		handleFormErrors: formErrorsHandler || defaultFormErrorsHandler,
	};
}
